import { useId, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { XCircle } from "lucide-react";

import { ApiError, formatCLP, formatDateTime } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Button, ConfirmDialog, controlClasses } from "../../components/ui";
import { anularPago, type FormaPago } from "./api";
import { refrescarTrasMovimientoDePago } from "./refrescar";

interface Props {
  pagoId: number;
  socioId: number;
  /** Datos del pago para que la confirmación muestre qué se anula. */
  monto: number;
  fecha: string;
  forma: FormaPago;
}

export function AnularPagoButton({
  pagoId,
  socioId,
  monto,
  fecha,
  forma,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const motivoId = useId();
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (m: string) => anularPago(pagoId, m),
    onSuccess: () => {
      toast.success(`Pago de ${formatCLP(monto)} anulado`);
      cerrar();
      refrescarTrasMovimientoDePago(qc, socioId);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo anular";
      setError(msg);
      toast.error(msg);
    },
  });

  function cerrar(): void {
    setOpen(false);
    setMotivo("");
    setError(null);
  }

  function confirmar(): void {
    setError(null);
    if (motivo.trim().length < 3) {
      setError("Escribe el motivo (al menos 3 letras).");
      return;
    }
    mutation.mutate(motivo.trim());
  }

  return (
    <>
      <Button
        variant="linkDanger"
        size="sm"
        icon={XCircle}
        onClick={() => setOpen(true)}
        title="Anular pago"
      >
        Anular
      </Button>
      <ConfirmDialog
        open={open}
        title="Anular pago"
        confirmLabel={`Anular pago de ${formatCLP(monto)}`}
        loading={mutation.isPending}
        onCancel={cerrar}
        onConfirm={confirmar}
        description={
          <div className="space-y-3">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt>Monto</dt>
              <dd className="font-semibold text-neutral-900 tabular-nums">
                {formatCLP(monto)}
              </dd>
              <dt>Fecha</dt>
              <dd>{formatDateTime(fecha)}</dd>
              <dt>Forma</dt>
              <dd>{forma}</dd>
            </dl>
            <p>
              Las boletas que cubría vuelven a quedar pendientes y el monto sale
              de la caja de ese día. La anulación queda registrada con tu nombre.
            </p>
            <div className="space-y-1">
              <label htmlFor={motivoId} className="block text-sm font-medium text-neutral-700">
                Motivo de la anulación
              </label>
              <input
                id={motivoId}
                type="text"
                autoFocus
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej: se registró al socio equivocado"
                maxLength={255}
                disabled={mutation.isPending}
                aria-invalid={error ? true : undefined}
                className={controlClasses}
              />
              {error && (
                <p role="alert" className="text-xs text-rose-700">
                  {error}
                </p>
              )}
            </div>
          </div>
        }
      />
    </>
  );
}
